import { useGoogleLogin } from "@react-oauth/google";
import { useState } from "react";

import { AppConfig } from "~/AppConfig";
import { useAuth } from "~/contexts/AuthProvider";
import { queueReditusConversion } from "~/tracking/reditus";
import {
  clearInviteUuid,
  clearRegistrationParams,
  getRegistrationParams,
} from "~/utils/registration/params";

import { API_PATH } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import Button from "../../../components/mantine/button/Button";
import Spinner from "../../../components/ui/spinner/Spinner";

interface GoogleLoginButtonProps {
  buttonText?: string;
  isLoading?: boolean;
  disabled?: boolean;
  // Hands the auth code to the caller instead of signing in via /oauth/google.
  onCode?: (code: string) => Promise<void>;
}
const baseUrl = AppConfig.API_URL + API_PATH;

const GoogleLoginButton = ({
  buttonText = "Log in with Google",
  isLoading = false,
  disabled = false,
  onCode,
}: GoogleLoginButtonProps) => {
  const params = getRegistrationParams();
  const [isGoogleLoading, setGoogleLoading] = useState(false);
  const { setToken } = useAuth();
  const login = useGoogleLogin({
    // GIS always sends prompt=consent + access_type=offline for this flow (a
    // `prompt` option is ignored), so every code yields a refresh token.
    flow: "auth-code",
    onSuccess: async (codeResponse) => {
      if (onCode) {
        try {
          await onCode(codeResponse.code);
        } catch (e) {
          console.error(e);
        } finally {
          setGoogleLoading(false);
        }

        return;
      }

      try {
        const controller = new AbortController();

        const result = await fetch(`${baseUrl}/oauth/google`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...params,
            ...codeResponse,
          }),
          signal: controller.signal,
        });

        if (!result.ok) {
          throw new Error("Authentication failed");
        }

        const authData = await result.json();

        clearRegistrationParams();

        // send Reditus call if a new user was just created
        if (result.status === 201) {
          queueReditusConversion();
          clearInviteUuid();
        }

        // Navigation is intentionally handled by GuestOnlyRoute (which honours
        // `location.state.from`) — matches LoginPage/SignUpPage.
        setToken(authData?.access_token?.key);
      } catch (e) {
        console.error(e);
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      // common OAuth errors
      setGoogleLoading(false);
    },
    onNonOAuthError: () => {
      // cases when user closed popup/iframe, blocked by browser, etc.
      setGoogleLoading(false);
    },
  });

  return (
    <Button
      onClick={() => {
        setGoogleLoading(true);
        login();
      }}
      variant="outlined"
      fullWidth
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
      disabled={disabled}
    >
      {isLoading || isGoogleLoading ? (
        <Spinner size="1rem" />
      ) : (
        <Icon iconName="GoogleIcon" />
      )}
      {buttonText}
    </Button>
  );
};

export default GoogleLoginButton;
